import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "../client";

/** What to render for a partner. Sent by name so a new member does not shift a number. */
export type PartnerOfferVisibility =
  | "ShowOffer"
  | "ShowSignUpFollowUp"
  | "Hidden";

export type PartnerOfferStatus =
  | "Unknown"
  | "Snoozed"
  | "AlreadyUsing"
  | "ClickedThrough"
  | "SignedUpViaScryer";

/** What the customer did. The action, not the status it produces — the API decides that. */
export type PartnerOfferDecision =
  | "Snooze"
  | "AlreadyUsing"
  | "ClickThrough"
  | "ConfirmSignedUp"
  | "Reopen";

export interface PartnerOfferDto {
  partnerKey: string;
  referralLink: string;
  /** The customer's discount code at the partner. Empty when there isn't one — don't render it. */
  couponCode: string;
  /** The incentive line. Empty until the terms are confirmed; the offer reads fine without it. */
  offerText: string;
  /**
   * Where "how this works" points — usually a portal help article path such as
   * `/help/how-it-works`. Empty when there is nothing worth linking to, in which case the
   * link is not rendered at all.
   */
  learnMoreUrl: string;
  status: PartnerOfferStatus;
  visibility: PartnerOfferVisibility;
  snoozedUntil: string | null;
}

export interface PartnerOffersDto {
  partners: PartnerOfferDto[];
  /**
   * Which partners the dashboard shows, in journey order. Capped by the API, so the dashboard does
   * not grow a column of offers as the catalogue does. Empty when none is due.
   */
  dashboardPartnerKeys: string[];
}

const PARTNER_OFFERS_KEY = ["partner-offers"] as const;

export function usePartnerOffers() {
  return useQuery<PartnerOffersDto>({
    queryKey: PARTNER_OFFERS_KEY,
    queryFn: async () => {
      const res = await apiClient.get<PartnerOffersDto>("/partner-offers/me");
      return res.data;
    },
    // A partner offer is never the reason someone opened the portal. If this fails, the dashboard
    // should still render everything else rather than retrying in the background.
    retry: false,
  });
}

export function useRecordPartnerOfferDecision() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      partnerKey: string;
      decision: PartnerOfferDecision;
    }) => {
      const res = await apiClient.post<PartnerOfferDto>(
        `/partner-offers/me/${encodeURIComponent(input.partnerKey)}/decisions`,
        { decision: input.decision }
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PARTNER_OFFERS_KEY });
    },
  });
}

/** The partners the dashboard should show, in the order the API gave them. Empty when none is due. */
export function dashboardOffers(
  data: PartnerOffersDto | undefined
): PartnerOfferDto[] {
  if (!data) return [];

  // Resolved against the list rather than sent twice, and a key with no partner behind it is
  // dropped rather than rendered as a blank card.
  return data.dashboardPartnerKeys
    .map((key) => data.partners.find((p) => p.partnerKey === key))
    .filter((offer): offer is PartnerOfferDto => offer !== undefined);
}
